import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, X, AlertCircle } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';

const ProductDetailsManagement = ({ pendingProductId, onConsumed }) => {
  const [details, setDetails] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDetail, setEditingDetail] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    productId: '',
    paymentMode: '',
    tenureMonths: '',
    interestRate: '',
    status: 'Active',
    extraDetailsJson: '{}',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [detailsRes, availableRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/investor-product-details`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/investor-product-details/available-products`, { headers: getAuthHeaders() }),
      ]);

      const detailsResult = await detailsRes.json();
      const availableResult = await availableRes.json();

      if (detailsResult.success) setDetails(detailsResult.data);
      if (availableResult.success) setAvailableProducts(availableResult.data);
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // When pendingProductId is set (e.g. after creating a product), refetch and open create modal with that product pre-selected
  useEffect(() => {
    if (pendingProductId) {
      onConsumed?.();
      (async () => {
        await fetchData();
        setFormData((prev) => ({
          ...prev,
          productId: String(pendingProductId),
        }));
        setShowCreateModal(true);
      })();
    }
  }, [pendingProductId, onConsumed]);

  const resetForm = () => {
    setFormData({
      productId: pendingProductId ? String(pendingProductId) : '',
      paymentMode: '',
      tenureMonths: '',
      interestRate: '',
      status: 'Active',
      extraDetailsJson: '{}',
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setError('');
      let extraDetails = null;
      if (formData.extraDetailsJson.trim()) {
        try {
          extraDetails = JSON.parse(formData.extraDetailsJson);
        } catch {
          setError('Extra details must be valid JSON');
          return;
        }
      }

      const response = await fetch(`${getApiUrl()}/api/investor-product-details`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          productId: parseInt(formData.productId),
          paymentMode: formData.paymentMode,
          tenureMonths: parseInt(formData.tenureMonths),
          interestRate: parseFloat(formData.interestRate),
          status: formData.status,
          extraDetails,
        }),
      });

      const result = await response.json();

      if (result.success) {
        resetForm();
        setShowCreateModal(false);
        fetchData();
      } else {
        setError(result.message || 'Failed to create product details');
      }
    } catch (err) {
      console.error('Create product details error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditClick = (detail) => {
    setEditingDetail(detail);
    setFormData({
      productId: String(detail.productId),
      paymentMode: detail.paymentMode,
      tenureMonths: String(detail.tenureMonths),
      interestRate: String(detail.interestRate),
      status: detail.status,
      extraDetailsJson: detail.extraDetails
        ? JSON.stringify(detail.extraDetails, null, 2)
        : '{}',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setError('');
      let extraDetails = null;
      if (formData.extraDetailsJson.trim()) {
        try {
          extraDetails = JSON.parse(formData.extraDetailsJson);
        } catch {
          setError('Extra details must be valid JSON');
          return;
        }
      }

      const response = await fetch(`${getApiUrl()}/api/investor-product-details/${editingDetail.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          paymentMode: formData.paymentMode,
          tenureMonths: parseInt(formData.tenureMonths),
          interestRate: parseFloat(formData.interestRate),
          status: formData.status,
          extraDetails,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        setEditingDetail(null);
        resetForm();
        fetchData();
      } else {
        setError(result.message || 'Failed to update product details');
      }
    } catch (err) {
      console.error('Update product details error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/investor-product-details/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDeleteConfirm(null);
        fetchData();
      } else {
        setError(result.message || 'Failed to delete product details');
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete product details error:', err);
      setError('Unable to connect to server');
      setDeleteConfirm(null);
    }
  };

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination(details, 10);

  if (loading && details.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const modalForm = (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={editingDetail ? 'hidden' : 'md:col-span-2'}>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Product *</label>
          <StyledDropdown
            options={[{ value: '', label: 'Select product' }, ...availableProducts.map((p) => ({ value: String(p.id), label: p.name }))]}
            value={formData.productId ? String(formData.productId) : ''}
            onChange={(val) => setFormData({ ...formData, productId: val })}
            placeholder="Select product"
            className="w-full"
            disabled={!!editingDetail}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Payment mode (dividend frequency) *</label>
          <StyledDropdown
            options={[
              { value: 'Monthly', label: 'Monthly' },
              { value: 'Quarterly', label: 'Quarterly' },
              { value: 'Yearly', label: 'Yearly' },
            ]}
            value={formData.paymentMode}
            onChange={(val) => setFormData({ ...formData, paymentMode: val })}
            placeholder="Select payment mode"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Tenure (months) *</label>
          <input
            type="number"
            min="1"
            value={formData.tenureMonths}
            onChange={(e) => setFormData({ ...formData, tenureMonths: e.target.value })}
            className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Interest rate (% p.a.) *</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.interestRate}
            onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
            className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Status</label>
          <StyledDropdown
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
            value={formData.status}
            onChange={(val) => setFormData({ ...formData, status: val })}
            placeholder="Select status"
            className="w-full"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Extra details (JSON, optional)</label>
          <p className="text-xs text-gray-500 dark:text-dark-muted mb-2">
            Product-specific attributes that don&apos;t have their own columns above. Use valid JSON: keys and string values in double quotes, numbers without quotes. Leave as {'{}'} if not needed.
          </p>
          <textarea
            value={formData.extraDetailsJson}
            onChange={(e) => setFormData({ ...formData, extraDetailsJson: e.target.value })}
            className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
            rows={5}
            placeholder={'{\n  "class": "Class A",\n  "min_amount": 10000,\n  "max_amount": 500000,\n  "description": "Unit trust fund"\n}'}
          />
          <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
            Sample: <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{'{"class": "Class A", "min_amount": 10000}'}</code>
          </p>
        </div>
      </div>
    </>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Product Details</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">Investor-facing product details (payment mode, tenure, interest rate). Dividend frequency comes from payment mode.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          Add Product Details
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Product</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Payment mode</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Tenure (months)</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Interest rate</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Status</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted">
                    <Package size={48} className="mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                    <p className="text-lg font-medium">No product details yet</p>
                    <p className="text-sm">Add details for products so investors can enroll. You can add manually here or after creating a product.</p>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-dark-text">{d.product?.name ?? '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">{d.paymentMode}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-dark-text">{d.tenureMonths}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-dark-text">{parseFloat(d.interestRate).toFixed(2)}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${d.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-dark-muted'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button type="button" onClick={() => handleEditClick(d)} className="text-primary-600 hover:text-primary-800 p-1 mr-2" title="Edit">
                        <Edit size={18} />
                      </button>
                      <button type="button" onClick={() => setDeleteConfirm(d)} className="text-red-600 hover:text-red-800 p-1" title="Delete">
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={10} onPageChange={handlePageChange} />
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Add Product Details</h2>
              <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-4">
              {modalForm}
              <div className="flex gap-3 pt-4 border-t dark:border-dark-border">
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showEditModal && editingDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Edit Product Details — {editingDetail.product?.name}</h2>
              <button type="button" onClick={() => { setShowEditModal(false); setEditingDetail(null); resetForm(); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              {modalForm}
              <div className="flex gap-3 pt-4 border-t dark:border-dark-border">
                <button type="button" onClick={() => { setShowEditModal(false); setEditingDetail(null); resetForm(); }} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-md p-4">
            <p className="text-gray-700 dark:text-dark-text mb-4">
              Delete product details for <strong>{deleteConfirm.product?.name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                Cancel
              </button>
              <button type="button" onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsManagement;
